import { ReactNode } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import RNModal from 'react-native-modal/dist/modal';
import { Text } from '@/components/Text';
import { Icon } from '@/components/Icon';
import { allowNativeDriver } from '@/utils/allowNativeDriver';
import { styles } from './style';

interface ModalProps {
  title?: string;
  isVisible: boolean;
  onClose?: () => void;
  confirmActionButton?: ReactNode;
  cancelActionButton?: ReactNode;
  children: ReactNode;
  danger?: boolean;
  headingComponent?: ReactNode;
  showCancelX?: boolean;
  cancelOnBackdropPress?: boolean;
  /** Bound the content in a scroll view of fixed max height (custom content). */
  boundContent?: boolean;
  /** Widen past the default 768 cap for large content (e.g. a chart). */
  wide?: boolean;
}

/*
 * Patient-app modal shell. Mirrors the therapist Modal API (title + children +
 * confirm/cancel button slots) so the shared Dialog can pick a shell and pass
 * the same rendered button nodes. Purely presentational — button visibility is
 * decided upstream in createDialogButtons.
 */
export const Modal = ({
  title,
  isVisible,
  onClose,
  confirmActionButton,
  cancelActionButton,
  children,
  danger = false,
  headingComponent,
  showCancelX = false,
  cancelOnBackdropPress,
  boundContent,
  wide,
}: ModalProps) => {
  function handlePressBackdrop() {
    if (!cancelOnBackdropPress) {
      return;
    }
    onClose?.();
  }

  const showButtonRow = !!confirmActionButton || !!cancelActionButton;

  return (
    <RNModal
      isVisible={isVisible}
      statusBarTranslucent
      onBackdropPress={handlePressBackdrop}
      useNativeDriver={allowNativeDriver()}
      useNativeDriverForBackdrop={allowNativeDriver()}
      hideModalContentWhileAnimating
      avoidKeyboard
      style={wide ? styles.wideModal : undefined}
    >
      <View style={[styles.container, wide && styles.containerWide]}>
        {!!headingComponent && headingComponent}
        {!!title && (
          <View
            style={[
              styles.titleRow,
              danger ? styles.titleRowDanger : styles.titleRowNeutral,
            ]}
          >
            <Text style={danger ? styles.headerDanger : styles.headerNeutral}>
              {title}
            </Text>
            {showCancelX ? (
              <Pressable
                onPress={onClose}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                style={styles.closeXButton}
              >
                <Icon name="x" size={24} color={danger ? 'white' : undefined} />
              </Pressable>
            ) : null}
          </View>
        )}
        <ScrollView>{children}</ScrollView>

        {showButtonRow ? (
          <View style={styles.buttonRow}>
            {confirmActionButton}
            {cancelActionButton}
          </View>
        ) : null}
      </View>
    </RNModal>
  );
};
